import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Panel, Badge } from "@/components/Bits";
import {
  ScanLine, Send, RefreshCw, CheckCircle2, XCircle, Clock,
  Loader2, Copy, AlertCircle, Settings as SettingsIcon, Save, Globe2,
} from "lucide-react";
import { toast } from "sonner";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

const STATUS_META = {
  WAITING_CALLBACK: { color: "awaiting", icon: Clock, label: "Waiting Callback" },
  SUCCESS:          { color: "success", icon: CheckCircle2, label: "Success" },
  FAILED:           { color: "error", icon: XCircle, label: "Failed" },
  EXPIRED:          { color: "muted", icon: XCircle, label: "Expired" },
  CANCELLED:        { color: "muted", icon: XCircle, label: "Cancelled" },
};

export default function HealthCheck() {
  const [tab, setTab] = useState("test");
  const [egress, setEgress] = useState(null);
  const [egressBusy, setEgressBusy] = useState(false);

  const checkEgress = async () => {
    setEgressBusy(true);
    try {
      const { data } = await api.get("/system/egress-ip");
      setEgress(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setEgressBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="healthcheck-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Health Check</div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ScanLine className="w-6 h-6 text-cyan" /> QRIS Gateway Test
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Generate test QRIS dari Tarapay / Saturnuspay sandbox dan monitor waktu callback. Berguna untuk cek health sistem payment gateway.
          </p>
        </div>
        <div className="flex items-center gap-1 border border-line rounded-sm p-0.5">
          <TabBtn active={tab === "test"} onClick={() => setTab("test")} testid="hc-tab-test"><ScanLine className="w-3.5 h-3.5" /> Generate</TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="hc-tab-history"><Clock className="w-3.5 h-3.5" /> History</TabBtn>
          <TabBtn active={tab === "config"} onClick={() => setTab("config")} testid="hc-tab-config"><SettingsIcon className="w-3.5 h-3.5" /> Provider Config</TabBtn>
        </div>
      </div>

      {/* Egress IP checker */}
      <div className="bg-surface border border-line rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid="egress-ip-panel">
        <Globe2 className="w-4 h-4 text-cyan flex-shrink-0" />
        <div className="text-xs">
          <span className="text-slate-400">Egress IP (untuk whitelist Tarapay & Saturnus):</span>{" "}
          {egress?.egress_ip ? (
            <code className="font-mono text-sm text-cyan font-bold ml-1" data-testid="egress-ip-value">
              {Array.isArray(egress.egress_ip) ? egress.egress_ip.join(" / ") : egress.egress_ip}
            </code>
          ) : (
            <span className="text-slate-500 ml-1">— belum dicek</span>
          )}
          {egress?.consistent === false && (
            <span className="ml-2 text-amber-400 text-[10px]">⚠ IP berubah antar request (NAT pool)</span>
          )}
        </div>
        <div className="flex-1" />
        {egress?.egress_ip && !Array.isArray(egress.egress_ip) && (
          <button
            data-testid="copy-egress-ip"
            onClick={() => { navigator.clipboard.writeText(egress.egress_ip); toast.success("IP disalin"); }}
            className="text-xs text-cyan hover:underline inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
        )}
        <button
          data-testid="check-egress-btn"
          onClick={checkEgress}
          disabled={egressBusy}
          className="bg-bg border border-line hover:border-cyan/50 text-xs font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
        >
          {egressBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 text-cyan" />}
          {egress ? "Recheck" : "Cek IP Sekarang"}
        </button>
      </div>

      {tab === "test" && <TestTab />}
      {tab === "history" && <HistoryTab />}
      {tab === "config" && <ConfigTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${
        active ? "bg-cyan/10 text-cyan border border-cyan/40" : "border border-transparent text-slate-400 hover:text-white"
      }`}>{children}</button>
  );
}

// ---------- GENERATE TAB ----------
function TestTab() {
  const [provider, setProvider] = useState("TARAPAY");
  const [amount, setAmount] = useState("10000");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Tick clock for countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Poll order for callback updates
  useEffect(() => {
    if (!order || order.status !== "WAITING_CALLBACK") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/healthcheck/orders/${order.id}`);
        setOrder(data);
      } catch (_e) { /* ignore poll errors */ }
    }, 2500);
    return () => clearInterval(t);
  }, [order?.id, order?.status]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/healthcheck/generate", { provider, amount });
      setOrder(data);
      if (data.ok) toast.success(`QRIS dibuat — menunggu callback`);
      else toast.error(`Gateway response: ${data.response?.message || "failed"}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const secsLeft = useMemo(() => {
    if (!order?.expires_at) return null;
    const exp = new Date(order.expires_at).getTime();
    return Math.max(0, Math.floor((exp - now) / 1000));
  }, [order?.expires_at, now]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hc-test-tab">
      <Panel title="Generate Test QRIS" testid="hc-generator">
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Provider</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {["TARAPAY", "SATURNUS"].map((p) => (
                <button key={p} type="button" data-testid={`hc-provider-${p.toLowerCase()}`}
                  onClick={() => setProvider(p)}
                  className={`py-2 text-xs uppercase tracking-wider rounded-sm border ${
                    provider === p ? "bg-cyan/10 border-cyan/40 text-cyan" : "border-line text-slate-400 hover:bg-surface2"
                  }`}>{p}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Amount (IDR, min 10.000)</label>
            <input data-testid="hc-amount" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} className={`${ipt} font-mono`} placeholder="10000" />
          </div>
          <button data-testid="hc-generate-btn" disabled={busy || !amount} onClick={generate}
            className="w-full bg-cyan text-bg font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2 hover:bg-cyan-dark disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? "Generating..." : "Generate QRIS"}
          </button>
          <div className="text-[10px] text-slate-500 leading-relaxed">
            Pastikan credentials provider sudah di-set di tab &quot;Provider Config&quot;. Notify URL di-config otomatis ke <code className="text-cyan">/api/healthcheck/callback/&#123;provider&#125;</code>.
          </div>
        </div>
      </Panel>

      <Panel title="Current Order" testid="hc-current-order">
        {!order ? (
          <div className="text-center text-slate-500 text-sm py-10">Belum ada order. Klik Generate untuk mulai test.</div>
        ) : (
          <OrderDisplay order={order} secsLeft={secsLeft} />
        )}
      </Panel>
    </div>
  );
}

function OrderDisplay({ order, secsLeft }) {
  const sm = STATUS_META[order.status] || STATUS_META.WAITING_CALLBACK;
  const Icon = sm.icon;
  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };
  const cbs = order.callbacks || [];

  return (
    <div className="space-y-3" data-testid={`hc-order-${order.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${order.status === "WAITING_CALLBACK" ? "animate-pulse text-amber-400" : "text-cyan"}`} />
          <Badge color={sm.color}>{sm.label}</Badge>
          <Badge color="muted">{order.provider}</Badge>
          <Badge color="muted" className="!text-[9px]">{order.environment || "SANDBOX"}</Badge>
        </div>
        {order.status === "WAITING_CALLBACK" && secsLeft !== null && (
          <div className={`text-xs font-mono ${secsLeft < 60 ? "text-rose-400" : "text-slate-400"}`}>
            Expires in {Math.floor(secsLeft / 60)}m {secsLeft % 60}s
          </div>
        )}
      </div>

      {order.qr_image_data_url && (
        <div className="bg-white p-3 rounded-sm flex items-center justify-center" data-testid="hc-qr-img">
          <img src={order.qr_image_data_url} alt="QRIS" className="w-56 h-56 object-contain" />
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <Row label="Merchant Order #" value={order.merchant_order_no} copy={copy} />
        <Row label="Platform Order #" value={order.platform_order_no} copy={copy} />
        <Row label="Amount" value={`IDR ${parseInt(order.amount || 0).toLocaleString("id-ID")}`} />
        <Row label="Pay Code / VA" value={order.pay_code} copy={copy} mono />
        <Row label="Created" value={new Date(order.created_at).toLocaleString("id-ID")} />
        {order.first_callback_at && (
          <Row label="First Callback" value={`${new Date(order.first_callback_at).toLocaleString("id-ID")} (${order.callback_latency_ms ?? "?"} ms)`} />
        )}
      </div>

      {!order.ok && order.response?.message && (
        <div className="border border-rose-500/30 bg-rose-500/10 rounded-sm p-3 text-xs">
          <div className="flex items-center gap-2 text-rose-400 font-semibold mb-1">
            <AlertCircle className="w-3.5 h-3.5" /> Gateway Error
          </div>
          <div className="text-rose-200">{order.response.message}</div>
          <pre className="text-[10px] text-rose-200/60 mt-1 overflow-x-auto">{JSON.stringify(order.response, null, 2)}</pre>
        </div>
      )}

      {cbs.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Callbacks Received ({cbs.length})</div>
          <div className="space-y-2">
            {cbs.map((cb, i) => (
              <div key={i} className="border border-emerald-500/30 bg-emerald-500/10 rounded-sm p-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] text-emerald-300 font-mono">{cb.method} · {new Date(cb.received_at).toLocaleString("id-ID")}</span>
                  <Badge color="success" className="!text-[9px]">CALLBACK</Badge>
                </div>
                <pre className="text-[10px] text-slate-200 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">{JSON.stringify(cb.payload, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, copy, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 border-b border-line/40 py-1">
      <span className="text-slate-500 text-[10px] uppercase tracking-wider min-w-[110px]">{label}</span>
      <span className={`flex-1 truncate ${mono ? "font-mono" : ""}`}>{value}</span>
      {copy && <button onClick={() => copy(String(value))} className="text-slate-500 hover:text-cyan"><Copy className="w-3 h-3" /></button>}
    </div>
  );
}

// ---------- HISTORY TAB ----------
function HistoryTab() {
  const [orders, setOrders] = useState([]);
  const load = async () => {
    const { data } = await api.get("/healthcheck/orders");
    setOrders(data);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);
  const [openId, setOpenId] = useState(null);

  return (
    <div className="space-y-2" data-testid="hc-history-tab">
      {orders.length === 0 && <div className="text-center text-slate-500 text-sm py-10">Belum ada test.</div>}
      {orders.map((o) => {
        const sm = STATUS_META[o.status] || STATUS_META.WAITING_CALLBACK;
        const Icon = sm.icon;
        const isOpen = openId === o.id;
        return (
          <div key={o.id} className="bg-surface border border-line rounded-sm" data-testid={`hc-history-${o.id}`}>
            <button onClick={() => setOpenId(isOpen ? null : o.id)} className="w-full flex items-center justify-between p-3 hover:bg-surface2 text-left">
              <div className="flex items-center gap-3">
                <Icon className={`w-4 h-4 ${o.status === "WAITING_CALLBACK" ? "text-amber-400 animate-pulse" : "text-slate-400"}`} />
                <div>
                  <div className="text-sm font-mono">{o.merchant_order_no}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <Badge color="muted" className="!text-[9px]">{o.provider}</Badge>
                    <span>IDR {parseInt(o.amount || 0).toLocaleString("id-ID")}</span>
                    <span>· {new Date(o.created_at).toLocaleString("id-ID")}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                {o.callback_latency_ms != null && <span className="text-emerald-400 font-mono">{o.callback_latency_ms} ms</span>}
                <Badge color={sm.color}>{sm.label}</Badge>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-line p-3">
                <OrderDisplay order={o} secsLeft={null} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------- CONFIG TAB ----------
function ConfigTab() {
  const [configs, setConfigs] = useState([]);
  const load = async () => {
    const { data } = await api.get("/healthcheck/configs");
    const byProv = Object.fromEntries(data.map((c) => [c.provider, c]));
    setConfigs(["TARAPAY", "SATURNUS"].map((p) => byProv[p] || { provider: p, environment: "SANDBOX", enabled: true, endpoint_path: "/v1/pay-in/create" }));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hc-config-tab">
      {configs.map((c) => <ProviderForm key={c.provider} initial={c} onSaved={load} />)}
    </div>
  );
}

function ProviderForm({ initial, onSaved }) {
  const defaultPrefix = initial.provider === "TARAPAY" ? "TARAPAY-SHA256withRSA" : "SATURNUSPAY-SHA256withRSA";
  const defaultReqId  = initial.provider === "TARAPAY" ? "tarapay-request-id"   : "saturnuspay-request-id";
  const [f, setF] = useState({
    app_id: initial.app_id || "",
    merchant_no: initial.merchant_no || "",
    private_key: "",  // never display existing; only paste new
    public_key: "",
    secret_key: "",
    product_code: initial.product_code ?? "",
    base_url: initial.base_url || (initial.provider === "TARAPAY" ? "https://sandbox.tarapay-id.com" : "https://sandbox.saturnuspay.com"),
    endpoint_path: initial.endpoint_path || "/pay-in/create",
    notify_url: initial.notify_url || "",
    auth_prefix: initial.auth_prefix || defaultPrefix,
    request_id_header: initial.request_id_header || defaultReqId,
    sign_header: initial.sign_header || "Authorization",
    sign_algorithm: initial.sign_algorithm || "SHA256withRSA",
    verify_callback_signature: initial.verify_callback_signature || false,
    environment: initial.environment || "SANDBOX",
    enabled: initial.enabled !== false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, product_code: f.product_code ? parseInt(f.product_code, 10) : null };
      // Drop empty key fields so existing stored value is preserved
      for (const k of ["private_key", "public_key", "secret_key"]) {
        if (!body[k] || !body[k].trim()) delete body[k];
      }
      await api.put(`/healthcheck/configs/${initial.provider}`, body);
      toast.success(`${initial.provider} config saved`);
      onSaved();
      setF({ ...f, private_key: "", public_key: "", secret_key: "" });
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-surface border border-line rounded-sm p-4 space-y-3" data-testid={`hc-config-${initial.provider}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold">{initial.provider}</h3>
        <div className="flex items-center gap-2">
          <Badge color={initial.environment === "PRODUCTION" ? "warning" : "muted"} className="!text-[9px]">
            {initial.environment || "SANDBOX"}
          </Badge>
          <Badge color={initial.private_key_set ? "success" : "warning"} className="!text-[9px]">
            {initial.private_key_set ? "Private Key Set" : "No Private Key"}
          </Badge>
          <label className="text-[10px] flex items-center gap-1 text-slate-300">
            <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} />
            Enabled
          </label>
        </div>
      </div>

      <details open className="space-y-2">
        <summary className="text-[10px] uppercase tracking-wider text-cyan cursor-pointer py-1">1 · Identity</summary>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">App ID *</label>
            <input data-testid={`hc-${initial.provider}-app_id`} value={f.app_id} onChange={(e) => setF({ ...f, app_id: e.target.value })} className={`${ipt} font-mono`} placeholder="gaukdjshfldashfk..." />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Merchant No (opsional)</label>
            <input data-testid={`hc-${initial.provider}-merchant_no`} value={f.merchant_no} onChange={(e) => setF({ ...f, merchant_no: e.target.value })} className={`${ipt} font-mono`} placeholder="kalau gateway minta mchNo terpisah" />
          </div>
        </div>
      </details>

      <details open className="space-y-2">
        <summary className="text-[10px] uppercase tracking-wider text-cyan cursor-pointer py-1">2 · RSA Keys</summary>
        <div className="space-y-2 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
              <span>Merchant Private Key (PKCS8 PEM) — untuk SIGN outbound request</span>
              {initial.private_key_set && <span className="text-emerald-400">✓ stored</span>}
            </label>
            <textarea data-testid={`hc-${initial.provider}-private_key`}
              rows={4} value={f.private_key}
              onChange={(e) => setF({ ...f, private_key: e.target.value })}
              placeholder={initial.private_key_set ? `(stored — kosongkan untuk pertahankan, paste baru untuk replace)` : "-----BEGIN PRIVATE KEY-----\n..."}
              className={`${ipt} font-mono text-[10px]`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
              <span>Gateway Public Key (PEM) — untuk VERIFY inbound callback</span>
              {initial.public_key_set && <span className="text-emerald-400">✓ stored</span>}
            </label>
            <textarea data-testid={`hc-${initial.provider}-public_key`}
              rows={4} value={f.public_key}
              onChange={(e) => setF({ ...f, public_key: e.target.value })}
              placeholder={initial.public_key_set ? `(stored)` : "-----BEGIN PUBLIC KEY-----\n..."}
              className={`${ipt} font-mono text-[10px]`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
              <span>Secret / HMAC Key (opsional)</span>
              {initial.secret_key_set && <span className="text-emerald-400">✓ stored</span>}
            </label>
            <input data-testid={`hc-${initial.provider}-secret_key`} value={f.secret_key} onChange={(e) => setF({ ...f, secret_key: e.target.value })} className={`${ipt} font-mono`} placeholder={initial.secret_key_set ? "(stored — kosongkan untuk pertahankan)" : "kalau gateway pakai shared secret tambahan"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Auth Prefix (outbound)</label>
              <input data-testid={`hc-${initial.provider}-auth_prefix`} value={f.auth_prefix} onChange={(e) => setF({ ...f, auth_prefix: e.target.value })} className={`${ipt} font-mono text-[11px]`} placeholder="TARAPAY-SHA256withRSA" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Request-ID Header</label>
              <input data-testid={`hc-${initial.provider}-request_id_header`} value={f.request_id_header} onChange={(e) => setF({ ...f, request_id_header: e.target.value })} className={`${ipt} font-mono text-[11px]`} placeholder="tarapay-request-id" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Sign Algorithm</label>
              <select value={f.sign_algorithm} onChange={(e) => setF({ ...f, sign_algorithm: e.target.value })} className={ipt}>
                <option value="SHA256withRSA">SHA256withRSA</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Callback Sign Header</label>
              <input value={f.sign_header} onChange={(e) => setF({ ...f, sign_header: e.target.value })} className={`${ipt} font-mono text-[11px]`} placeholder="Authorization" />
            </div>
            <div className="flex items-end">
              <label className="text-[10px] flex items-center gap-1 text-slate-300">
                <input type="checkbox" checked={f.verify_callback_signature} onChange={(e) => setF({ ...f, verify_callback_signature: e.target.checked })} />
                Verify callback signature
              </label>
            </div>
          </div>
        </div>
      </details>

      <details open className="space-y-2">
        <summary className="text-[10px] uppercase tracking-wider text-cyan cursor-pointer py-1">3 · Endpoint</summary>
        <div className="space-y-2 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Product Code (QRIS)</label>
              <input data-testid={`hc-${initial.provider}-product_code`} type="number" value={f.product_code} onChange={(e) => setF({ ...f, product_code: e.target.value })} className={`${ipt} font-mono`} placeholder="e.g. 11" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Environment</label>
              <select value={f.environment} onChange={(e) => setF({ ...f, environment: e.target.value })} className={ipt}>
                <option value="SANDBOX">Sandbox</option>
                <option value="PRODUCTION">Production</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Base URL</label>
            <input data-testid={`hc-${initial.provider}-base_url`} value={f.base_url} onChange={(e) => setF({ ...f, base_url: e.target.value })} className={`${ipt} font-mono`} placeholder={initial.provider === "TARAPAY" ? "https://sandbox.tarapay-id.com" : "https://sandbox.saturnuspay.com"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Endpoint Path</label>
            <input value={f.endpoint_path} onChange={(e) => setF({ ...f, endpoint_path: e.target.value })} className={`${ipt} font-mono`} placeholder="/v1/pay-in/create" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Notify URL Override (opsional)</label>
            <input value={f.notify_url} onChange={(e) => setF({ ...f, notify_url: e.target.value })} className={`${ipt} font-mono text-[11px]`} placeholder="(default: backend URL + /api/healthcheck/callback/...)" />
            <div className="text-[10px] text-slate-500 mt-1">
              Default callback yang harus didaftarkan di portal merchant:<br />
              <code className="text-cyan break-all">{(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/healthcheck/callback/{initial.provider.toLowerCase()}</code>
            </div>
          </div>
        </div>
      </details>

      <button data-testid={`hc-${initial.provider}-save`} disabled={busy} className="w-full bg-cyan text-bg font-semibold py-2 text-sm rounded-sm flex items-center justify-center gap-2 hover:bg-cyan-dark disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save {initial.provider}
      </button>
    </form>
  );
}
